import { Nullable } from '@dai/common'
import { TFnType } from '@tolgee/react'
import dayjs from 'dayjs'

import { ActionRepeatSchedule } from '@/graphql'

import { LocalizationHelpers } from './localization.helpers'

const getDayOfString = (
  t: (key: string, params?: Record<string, any>) => string,
  repeatSchedule: ActionRepeatSchedule,
  date?: string | null,
) => {
  const dateToUse = date ? dayjs(date.slice(0, 10)) : dayjs() // we only care about the date part, so we slice it to "YYYY-MM-DD"
  switch (repeatSchedule) {
    case ActionRepeatSchedule.WEEKLY:
      return dateToUse.format('dddd')
    case ActionRepeatSchedule.MONTHLY:
      return LocalizationHelpers.ordinalize(t, dateToUse.date())
    case ActionRepeatSchedule.YEARLY:
      return `${dateToUse.format('MMM')} ${LocalizationHelpers.ordinalize(t, dateToUse.date())}`
  }
}

const getONString = (
  t: (key: string, params?: Record<string, any>) => string,
  repeatSchedule: ActionRepeatSchedule,
  date?: string | null,
) => {
  // Returns a string based on the repeat schedule and date
  // For example, if the repeat schedule is ActionRepeatSchedule.DAILY, it returns an empty string
  // If the repeat schedule is ActionRepeatSchedule.WEEKLY, it returns a string with the day of the week
  // If the repeat schedule is ActionRepeatSchedule.MONTHLY, it returns a string with the day of the month
  // If the repeat schedule is ActionRepeatSchedule.YEARLY, it returns a string with the month and day of the month

  const dateToUse = date ? dayjs(date) : dayjs()
  switch (repeatSchedule) {
    case ActionRepeatSchedule.DAILY:
      return t('dateHelper.repeatSchedule.daily')
    case ActionRepeatSchedule.WEEKLY:
      return t('dateHelper.repeatSchedule.weekly', {
        day: dateToUse.format('dddd'),
      })
    case ActionRepeatSchedule.MONTHLY:
      return t('dateHelper.repeatSchedule.monthly', {
        day: LocalizationHelpers.ordinalize(t, dateToUse.date()),
      })
    case ActionRepeatSchedule.YEARLY:
      return t('dateHelper.repeatSchedule.yearly', {
        month: dateToUse.format('MMM'),
        day: LocalizationHelpers.ordinalize(t, dateToUse.date()),
      })
  }
}

const sortTime = (a: Nullable<string>, b: Nullable<string>) => {
  if (!a) {
    return b ? -1 : 0
  }
  if (!b) {
    return 1
  }
  const aTime = dayjs(`2025-01-01${a}`)
  const bTime = dayjs(`2025-01-01${b}`)
  if (aTime.isSame(bTime)) {
    return 0
  }
  return aTime.isBefore(bTime) ? -1 : 1
}

const formatTime = (time: string) => {
  return dayjs(`2025-01-01${time}`).format('h:mm a')
}

const getStartOfDayWithTimezone = (date: string) => {
  // date should be in the format "YYYY-MM-DD"
  return dayjs(date).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
}

const getEndOfDayWithTimezone = (date: string) => {
  // date should be in the format "YYYY-MM-DD"
  return dayjs(date).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
}

const isMonthlyDates = (dates: string[]) => {
  // dates are in the format "YYYY-MM-DD"
  return dates.every(date => {
    const day = dayjs(date).date()
    return day == 1
  })
}

const isYearlyDates = (dates: string[]) => {
  // dates are in the format "YYYY-MM-DD"
  return dates.every(date => {
    const month = dayjs(date).month()
    const day = dayjs(date).date()
    return month == 0 && day == 1
  })
}

const parseIsoDateWithoutTimezone = (isoDateString: string): number => {
  const dateStr = isoDateString.substring(0, 10) // Extract "YYYY-MM-DD"
  const [year, month, day] = dateStr.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

const filterFutureDates = <T extends Record<string, any>>(
  items: T[],
  dateKey: keyof T,
): T[] => {
  const todayYYYYMMDD = dayjs().format('YYYY-MM-DD')
  return items.filter(item => {
    const itemDateYYYYMMDD = String(item[dateKey]).substring(0, 10)
    return itemDateYYYYMMDD <= todayYYYYMMDD
  })
}

const isSingleDayToday = (startDate?: string, endDate?: string): boolean => {
  if (!startDate || !endDate) {
    return false
  }
  return startDate === endDate && startDate === dayjs().format('YYYY-MM-DD')
}

const yearOverYearTooltipText = (
  startDate: string,
  endDate: string,
  t: TFnType,
) => {
  const daysToSubtract = 364
  const startDateObj = dayjs(startDate)
  const endDateObj = dayjs(endDate)
  const yearOverYearStartDate = startDateObj.subtract(daysToSubtract, 'day')
  const yearOverYearEndDate = endDateObj.subtract(daysToSubtract, 'day')
  return t('tooltip.yearOverYearTooltip', {
    startDate: yearOverYearStartDate.format('MMM D, YYYY'),
    endDate: yearOverYearEndDate.format('MMM D, YYYY'),
  })
}

const getPreviousDateRange = (startDate: string, endDate: string) => {
  const daysBetween = dayjs(endDate).diff(dayjs(startDate), 'day') + 1 // to not include the startDate in the previousEndDate
  const previousStartDate = dayjs(startDate).subtract(daysBetween, 'day')
  const previousEndDate = dayjs(endDate).subtract(daysBetween, 'day')
  return { previousStartDate, previousEndDate }
}

const previousPeriodTooltipText = (
  startDate: string,
  endDate: string,
  t: TFnType,
) => {
  const { previousStartDate, previousEndDate } = getPreviousDateRange(
    startDate,
    endDate,
  )
  return t('tooltip.previousPeriodTooltip', {
    startDate: previousStartDate.format('MMM D, YYYY'),
    endDate: previousEndDate.format('MMM D, YYYY'),
  })
}

export const DateHelpers = {
  getONString,
  getDayOfString,
  sortTime,
  formatTime,
  getEndOfDayWithTimezone,
  getStartOfDayWithTimezone,
  isMonthlyDates,
  isYearlyDates,
  parseIsoDateWithoutTimezone,
  filterFutureDates,
  isSingleDayToday,
  yearOverYearTooltipText,
  getPreviousDateRange,
  previousPeriodTooltipText,
}
